/**
 * Export CSV for the A/B results page: the goal per version (saved or launch),
 * then the test's details and version URLs.
 */

import { formatChange, formatGoalRate, type GoalComparison, type GoalMetricResult } from "@/lib/experiments/goalMetric"
import { formatChance, formatRange } from "@/lib/experiments/bayes"

type Cell = string | number | null | undefined

const csvCell = (cell: Cell) => {
  const value = String(cell ?? "")
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function goalSection(title: string, result: GoalMetricResult, comparison: GoalComparison): Cell[][] {
  return [
    [title, result.label, result.isLaunch ? "launch goal" : "changed after launch"],
    ["Version", "Unique Visitors", result.unit === "money" ? "Revenue" : "Count", "Rate", "Change", "Likely change", "Chance to beat Control"],
    ...comparison.rows.map((r) => [
      r.label,
      r.exposure,
      result.unit === "money" ? r.events.toFixed(2) : r.events,
      formatGoalRate(r.rate, result.unit),
      r.isControl ? "" : formatChange(r.change, r.isNew),
      r.range ? formatRange(r.range) : "",
      r.chance == null ? "" : formatChance(r.chance),
    ]),
  ]
}

export function buildResultsCsv(input: {
  name: string
  goalLabel: string
  status: string
  createdAt: string
  days: number
  goal: { result: GoalMetricResult; comparison: GoalComparison }
  totalViews: Array<{ label: string; views: number | null }>
  urls: Array<{ label: string; url: string }>
}): string {
  const rows: Cell[][] = [
    ...goalSection("Goal", input.goal.result, input.goal.comparison),
    ["Winner", input.goal.comparison.winner ? input.goal.comparison.winner.label : "No clear winner"],
  ]
  rows.push(
    [],
    ["Total Views", ...input.totalViews.map((v) => `${v.label}: ${v.views ?? "n/a"}`)],
    [],
    ["Experiment details"],
    ["Name", input.name],
    ["Goal at launch", input.goalLabel],
    ["Created", new Date(input.createdAt).toLocaleDateString()],
    ["Duration (days)", input.days],
    ["Status", input.status],
    [],
    ["Version URLs"],
    ...input.urls.map((u) => [u.label, u.url]),
  )
  return rows.map((r) => r.map(csvCell).join(",")).join("\n")
}

export function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}
